// frontend/src/services/api.js

// Configuração da API
const API_URL = process.env.REACT_APP_API_URL || '';

// 🔹 NOVA: Configuração de timeout
const DEFAULT_TIMEOUT = 30000; // 30 segundos

export const API = {
  BASE_URL: API_URL,
  
  // 🔐 Autenticação
  AUTH: {
    LOGIN: `${API_URL}/auth/login`,
    REGISTER: `${API_URL}/auth/register`,
    LOGOUT: `${API_URL}/auth/logout`,
    REFRESH_TOKEN: `${API_URL}/auth/refresh-token`,
    ME: `${API_URL}/auth/me`
  },
  
  // 👤 Usuários
  USERS: {
    LIST: `${API_URL}/api/usuarios`,
    GET: (id) => `${API_URL}/api/usuarios/${id}`,
    CREATE: `${API_URL}/api/usuarios`,
    UPDATE: (id) => `${API_URL}/api/usuarios/${id}`,
    DELETE: (id) => `${API_URL}/api/usuarios/${id}`,
    TOGGLE: (id) => `${API_URL}/api/usuarios/${id}/toggle`,
    RESET_PASSWORD: (id) => `${API_URL}/api/usuarios/${id}/resetar-senha`,
    DELETE_PERMANENT: (id) => `${API_URL}/api/usuarios/${id}/permanente`
  },
  
  // 👤 Perfil
  PROFILE: {
    GET: `${API_URL}/api/perfil`,
    UPDATE: `${API_URL}/api/perfil`,
    CHANGE_PASSWORD: `${API_URL}/api/perfil/alterar-senha`
  },
  
  // 🏢 Empresas
  EMPRESAS: {
    LIST: `${API_URL}/api/empresas`,
    GET: (id) => `${API_URL}/api/empresas/${id}`,
    CREATE: `${API_URL}/api/empresas`,
    UPDATE: (id) => `${API_URL}/api/empresas/${id}`,
    DELETE: (id) => `${API_URL}/api/empresas/${id}`
  },
  
  // 📊 Dashboard
  DASHBOARD: {
    STATS: `${API_URL}/api/dashboard/stats`,
    GROWTH: `${API_URL}/api/dashboard/growth`
  }
};

// 🔹 MELHORIA 1: Função para refresh token
const refreshAuthToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return null;

    const response = await fetch(API.AUTH.REFRESH_TOKEN, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    });

    if (!response.ok) {
      throw new Error('Falha ao renovar token');
    }

    const data = await response.json();
    if (data.token) {
      localStorage.setItem('token', data.token);
      return data.token;
    }
    return null;
  } catch (error) {
    console.error('Erro ao renovar token:', error);
    // Se falhar, fazer logout
    authAPI.logout();
    return null;
  }
};

// 🔹 MELHORIA 2: Função principal com timeout e retry
export const apiRequest = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    }
  };
  
  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers
    }
  };

  // 🔹 NOVO: Controller para timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT);

  try {
    const response = await fetch(url, { 
      ...mergedOptions, 
      signal: controller.signal 
    });
    clearTimeout(timeoutId);

    const data = await response.json();

    // 🔹 NOVO: Logs apenas em desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      console.log(`📡 ${options.method || 'GET'} ${url}`, data);
    }
    
    // 🔹 NOVO: Se token expirou (401), tentar refresh automaticamente
    if (response.status === 401 && !options._retry) {
      console.log('🔄 Token expirado, tentando renovar...');
      const newToken = await refreshAuthToken();
      
      if (newToken) {
        // Tentar novamente com o novo token
        options._retry = true;
        options.headers = {
          ...options.headers,
          'Authorization': `Bearer ${newToken}`
        };
        return apiRequest(url, options);
      }
    }
    
    if (!response.ok) {
      throw new Error(data.error || 'Erro na requisição');
    }
    
    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    
    // 🔹 NOVO: Tratamento específico para timeout
    if (error.name === 'AbortError') {
      console.error('⏰ Timeout: A requisição excedeu o tempo limite');
      throw new Error('A requisição demorou muito para responder. Tente novamente.');
    }
    
    console.error('❌ Erro na requisição:', error);
    throw error;
  }
};

// Funções específicas para autenticação
export const authAPI = {
  login: async (email, senha) => {
    try {
      const response = await fetch(API.AUTH.LOGIN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, senha })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Erro no login');
      }
      
      // Salvar token
      if (data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('refreshToken', data.refreshToken);
        localStorage.setItem('user', JSON.stringify(data.usuario));
      }
      
      // 🔹 NOVO: Log de sucesso
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Login realizado com sucesso:', data.usuario?.nome);
      }
      
      return data;
    } catch (error) {
      console.error('❌ Erro no login:', error);
      throw error;
    }
  },
  
  register: async (dados) => {
    try {
      const response = await fetch(API.AUTH.REGISTER, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dados)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Erro no registro');
      }
      
      // 🔹 NOVO: Log de sucesso
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Registro realizado com sucesso:', data.usuario?.email);
      }
      
      return data;
    } catch (error) {
      console.error('❌ Erro no registro:', error);
      throw error;
    }
  },
  
  logout: () => {
    // 🔹 NOVO: Tentar notificar o backend sobre o logout
    try {
      const token = localStorage.getItem('token');
      if (token) {
        fetch(API.AUTH.LOGOUT, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => {});
      }
    } catch (error) {
      // Ignora erro no logout
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    window.location.href = '/login';
  },
  
  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },
  
  getToken: () => {
    return localStorage.getItem('token');
  },
  
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  }
};

// Funções para usuários
export const userAPI = {
  list: (page = 1, limit = 10, search = '', role = '') => {
    const params = new URLSearchParams({ page, limit });
    if (search) params.append('search', search);
    if (role) params.append('role', role);
    return apiRequest(`${API.USERS.LIST}?${params}`);
  },
  
  get: (id) => apiRequest(API.USERS.GET(id)),
  
  create: (data) => apiRequest(API.USERS.CREATE, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  
  update: (id, data) => apiRequest(API.USERS.UPDATE(id), {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  
  delete: (id) => apiRequest(API.USERS.DELETE(id), {
    method: 'DELETE'
  }),
  
  toggle: (id) => apiRequest(API.USERS.TOGGLE(id), {
    method: 'PATCH'
  }),
  
  resetPassword: (id, novaSenha) => apiRequest(API.USERS.RESET_PASSWORD(id), {
    method: 'POST',
    body: JSON.stringify({ novaSenha })
  })
};

// Funções para perfil
export const profileAPI = {
  get: () => apiRequest(API.PROFILE.GET),
  
  update: (data) => apiRequest(API.PROFILE.UPDATE, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  
  changePassword: (senha_atual, nova_senha) => apiRequest(API.PROFILE.CHANGE_PASSWORD, {
    method: 'POST',
    body: JSON.stringify({ senha_atual, nova_senha })
  })
};

// Funções para empresas
export const empresaAPI = {
  list: (page = 1, limit = 10, search = '') => {
    const params = new URLSearchParams({ page, limit });
    if (search) params.append('search', search);
    return apiRequest(`${API.EMPRESAS.LIST}?${params}`);
  },
  
  get: (id) => apiRequest(API.EMPRESAS.GET(id)),
  
  create: (data) => apiRequest(API.EMPRESAS.CREATE, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  
  update: (id, data) => apiRequest(API.EMPRESAS.UPDATE(id), {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  
  delete: (id) => apiRequest(API.EMPRESAS.DELETE(id), {
    method: 'DELETE'
  })
};

// Funções para dashboard
export const dashboardAPI = {
  stats: () => apiRequest(API.DASHBOARD.STATS),
  growth: () => apiRequest(API.DASHBOARD.GROWTH)
};

export default API;